export type GrupoEventoArbitral =
  | 'ABERTURA'
  | 'TRIAGEM'
  | 'PENDENCIA'
  | 'PAGAMENTO'
  | 'CITACAO'
  | 'NOTIFICACAO'
  | 'AR'
  | 'MANIFESTACAO'
  | 'DECURSO'
  | 'ARBITRO'
  | 'TERMOS'
  | 'SENTENCA'
  | 'TRANSITO'
  | 'OUTROS';

export interface EventoArbitralDefinicao {
  value: string;
  label: string;
  descricao: string;
  grupo: GrupoEventoArbitral;
  semEmissor?: boolean;
  /**
   * Evento que só serve para INICIAR um fluxo, nunca para retomar uma espera. É o caso dos
   * gatilhos por parte (o evento já nasce endereçado ao fluxo da Lexi que o consome): uma espera
   * por ele no fluxo do processo nunca retomaria. Ausente = serve aos dois usos, como hoje.
   */
  soIniciador?: true;
}

// Eventos sem publisher no back-end (censo do ADR publishers-eventos-espera,
// 10/07/2026): uma espera por eles nunca retoma. A lista encolhe conforme os
// lotes do ADR entregam os emissores.
export const EVENTOS_SEM_EMISSOR = new Set<string>([
  'arbitral.oficio_validado.v1',
  'arbitral.termo_imparcialidade_juntado.v1',
  'arbitral.termo_arbitragem_publicado.v1',
  'arbitral.sentenca_publicada.v1',
  'arbitral.embargos_recebidos.v1',
  'arbitral.desistencia.v1',
]);

export function eventoSemEmissor(value: string): boolean {
  return EVENTOS_SEM_EMISSOR.has(value);
}

export const EVENTOS_ARBITRAIS: EventoArbitralDefinicao[] = [
  {
    value: 'arbitral.created.v1',
    label: 'Processo criado',
    descricao: 'Processo arbitral foi criado pelo requerente — gatilho de início do fluxo.',
    grupo: 'ABERTURA',
  },
  {
    value: 'arbitral.triagem_iniciada.v1',
    label: 'Triagem iniciada',
    descricao: 'Secretaria iniciou a triagem do processo (ação "Iniciar triagem" no detalhe).',
    grupo: 'TRIAGEM',
  },
  {
    value: 'arbitral.triagem_aprovada.v1',
    label: 'Triagem aprovada',
    descricao: 'Secretaria aprovou a triagem do processo.',
    grupo: 'TRIAGEM',
  },
  {
    value: 'arbitral.triagem_negada.v1',
    label: 'Triagem negada',
    descricao: 'Secretaria negou a triagem (motivo estruturado).',
    grupo: 'TRIAGEM',
  },
  {
    value: 'arbitral.triagem_sanavel.v1',
    label: 'Triagem com pedido de correção',
    descricao: 'Secretaria pediu correção de documentação ao requerente.',
    grupo: 'TRIAGEM',
  },
  {
    value: 'arbitral.pendencia_resolvida.v1',
    label: 'Pendência resolvida',
    descricao: 'Uma pendência foi marcada como resolvida.',
    grupo: 'PENDENCIA',
  },
  {
    value: 'arbitral.pendencia_expirada.v1',
    label: 'Pendência expirada',
    descricao: 'Prazo da pendência venceu sem resposta.',
    grupo: 'PENDENCIA',
  },
  {
    value: 'arbitral.pagamento_confirmado.v1',
    label: 'Pagamento confirmado',
    descricao: 'Cobrança foi paga (boleto/pix/cartão).',
    grupo: 'PAGAMENTO',
  },
  {
    value: 'arbitral.oficio_validado.v1',
    label: 'Ofício validado por terceiro',
    descricao: 'Terceiro (imobiliária, banco, registro) respondeu ofício.',
    grupo: 'OUTROS',
  },
  {
    value: 'arbitral.citacao_enviada.v1',
    label: 'Citação enviada',
    descricao: 'Citação foi disparada para a parte (digital + AR).',
    grupo: 'CITACAO',
  },
  {
    value: 'arbitral.notificacao_entregue.v1',
    label: 'Notificação entregue',
    descricao: 'Provedor confirmou entrega (e-mail/WhatsApp).',
    grupo: 'NOTIFICACAO',
  },
  {
    value: 'arbitral.notificacao_bounce.v1',
    label: 'Notificação não entregue (bounce)',
    descricao: 'Endereço inválido ou falha de entrega.',
    grupo: 'NOTIFICACAO',
  },
  {
    value: 'arbitral.notificacao_opened.v1',
    label: 'Notificação aberta',
    descricao: 'Destinatário abriu o e-mail (tracking pixel).',
    grupo: 'NOTIFICACAO',
  },
  {
    value: 'arbitral.ar_retornado.v1',
    label: 'AR físico retornado',
    descricao: 'Scrybe confirmou retorno do AR (entregue/recusado/devolvido).',
    grupo: 'AR',
  },
  {
    value: 'arbitral.manifestacao_potencial.v1',
    label: 'Manifestação potencial recebida',
    descricao: 'Inbound (e-mail/WhatsApp/chat) aguardando triagem da Secretaria.',
    grupo: 'MANIFESTACAO',
  },
  {
    value: 'arbitral.manifestacao_registrada.v1',
    label: 'Manifestação registrada',
    descricao: 'Secretaria confirmou que inbound é manifestação válida.',
    grupo: 'MANIFESTACAO',
  },
  {
    value: 'arbitral.manifestacao_desconsiderada.v1',
    label: 'Manifestação desconsiderada',
    descricao: 'Secretaria descartou inbound com justificativa (G9).',
    grupo: 'MANIFESTACAO',
  },
  {
    value: 'arbitral.decurso_publicado.v1',
    label: 'Decurso publicado',
    descricao: 'Certidão de decurso de prazo foi emitida.',
    grupo: 'DECURSO',
  },
  {
    value: 'arbitral.arbitro_sorteado.v1',
    label: 'Árbitro sorteado',
    descricao: 'Sorteio confirmado e árbitro nomeado.',
    grupo: 'ARBITRO',
  },
  {
    value: 'arbitral.termo_imparcialidade_juntado.v1',
    label: 'Termo de imparcialidade juntado',
    descricao: 'Árbitro assinou o termo de imparcialidade.',
    grupo: 'TERMOS',
  },
  {
    value: 'arbitral.termo_arbitragem_publicado.v1',
    label: 'Termo de arbitragem publicado',
    descricao: 'Termo de arbitragem publicado, instrução iniciada.',
    grupo: 'TERMOS',
  },
  {
    value: 'arbitral.concluso_feito.v1',
    label: 'Concluso ao árbitro',
    descricao: 'Processo encaminhado para árbitro decidir.',
    grupo: 'ARBITRO',
  },
  {
    value: 'arbitral.sentenca_publicada.v1',
    label: 'Sentença publicada',
    descricao: 'Sentença arbitral foi publicada.',
    grupo: 'SENTENCA',
  },
  {
    value: 'arbitral.intimacao_sentenca_enviada.v1',
    label: 'Intimação de sentença enviada',
    descricao: 'Partes foram intimadas da sentença.',
    grupo: 'SENTENCA',
  },
  {
    value: 'arbitral.embargos_recebidos.v1',
    label: 'Embargos recebidos',
    descricao: 'Parte protocolou embargos pós-sentença.',
    grupo: 'SENTENCA',
  },
  {
    value: 'arbitral.transito_publicado.v1',
    label: 'Trânsito em julgado publicado',
    descricao: 'Certidão de trânsito emitida, ciclo encerrado.',
    grupo: 'TRANSITO',
  },
  {
    value: 'arbitral.transito_encaminhado_parte.v1',
    label: 'Trânsito encaminhado (parte)',
    descricao:
      'Certidão de trânsito encaminhada a cada requerido — gatilho do fluxo da Lexi que a envia.',
    grupo: 'TRANSITO',
    soIniciador: true,
  },
  {
    value: 'arbitral.desistencia.v1',
    label: 'Desistência',
    descricao: 'Requerente desistiu da arbitragem.',
    grupo: 'OUTROS',
  },
  {
    value: 'arbitral.prazo_vencido.v1',
    label: 'Prazo vencido',
    descricao: 'Algum prazo monitorado venceu (gerado por INICIAR_PRAZO).',
    grupo: 'OUTROS',
  },
  {
    value: 'arbitral.bloqueio_overridden.v1',
    label: 'Bloqueio com override',
    descricao:
      'Secretaria forçou um guard com justificativa (G1..G12, inclusive a liberação da trava da sentença).',
    grupo: 'OUTROS',
  },
];

/**
 * Eventos que um bloco de espera pode aguardar: o catálogo sem os que só iniciam fluxo. O gatilho
 * do "Novo fluxo" (`GatilhoSelect`) oferece o catálogo inteiro.
 */
export const EVENTOS_DE_ESPERA: readonly EventoArbitralDefinicao[] = EVENTOS_ARBITRAIS.filter(
  (evento) => !evento.soIniciador,
);

export const GRUPOS_EVENTO_LABEL: Record<GrupoEventoArbitral, string> = {
  ABERTURA: 'Abertura',
  TRIAGEM: 'Triagem',
  PENDENCIA: 'Pendências',
  PAGAMENTO: 'Pagamento',
  CITACAO: 'Citação',
  NOTIFICACAO: 'Notificações digitais',
  AR: 'AR físico',
  MANIFESTACAO: 'Manifestações',
  DECURSO: 'Decurso',
  ARBITRO: 'Árbitro',
  TERMOS: 'Termos',
  SENTENCA: 'Sentença',
  TRANSITO: 'Trânsito',
  OUTROS: 'Outros',
};

export const EVENTO_POR_VALUE = new Map<string, EventoArbitralDefinicao>(
  EVENTOS_ARBITRAIS.map((e) => [e.value, e]),
);

export function labelDoEvento(value: string): string {
  return EVENTO_POR_VALUE.get(value)?.label ?? value;
}
